import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicSchoolDto } from '@motoschool/shared';
import { useAuth } from '../auth/AuthContext';
import { api, ApiRequestError } from '../lib/api';
import { Button, ErrorNotice, Field, Input } from '../components/ui';
import { SchoolName } from '../components/SchoolName';
import { Logo } from '../components/Logo';
import { usePageMeta } from '../lib/seo';

export function LoginPage(): React.JSX.Element {
  // Вход в кабинет поиску не нужен: ни одного запроса, по которому его ищут
  usePageMeta({
    title: 'Вход для учеников — FREEDOM BIKER',
    description: 'Вход в личный кабинет мотошколы FREEDOM BIKER.',
    path: '/login',
    noindex: true,
  });

  const { login } = useAuth();
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Название приходит из настроек школы — то же, что видит гость на главной.
  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
    staleTime: 5 * 60_000,
  });

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(loginValue, password);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Не удалось войти, попробуйте позже',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <Logo className="mx-auto mb-4 size-20" />
          <h1>
            <SchoolName name={school?.name ?? 'Мотошкола'} className="text-xl" />
          </h1>
          <p className="mt-1 text-sm text-muted">Расписание занятий и учёт часов</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 rounded-card border border-line bg-surface p-5 shadow-sm">
          {error && <ErrorNotice message={error} />}

          <Field label="Email или телефон" required>
            <Input
              value={loginValue}
              onChange={(event) => setLoginValue(event.target.value)}
              autoComplete="username"
              inputMode="email"
              placeholder="ivan@example.com"
              required
            />
          </Field>

          <Field label="Пароль" required>
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          <Button type="submit" fullWidth isLoading={isSubmitting}>
            Войти
          </Button>
        </form>

        {/*
          Самостоятельной регистрации нет: аккаунт заводит школа либо он
          создаётся автоматически, когда инструктор подтвердит запись.
        */}
        <p className="mt-6 text-center text-sm text-muted">
          Ещё не занимаетесь у нас?{' '}
          <Link to="/schedule" className="font-medium text-brand-600">
            Выберите время в расписании
          </Link>{' '}
          — записаться можно без регистрации, аккаунт создаст инструктор.
        </p>
      </div>
    </div>
  );
}
